/**
 * WebchatConfigDialog - Modal de configuração completa do widget de webchat
 * 
 * Inclui 4 tabs:
 * - Visual: cores, logo, ícone do botão, posição, branding
 * - Comportamento: mensagens, placeholder, delay auto-abrir
 * - Coleta de Dados: toggles para nome/email/telefone
 * - Avançado: domínios permitidos, fluxo de boas-vindas, CSS customizado
 * 
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Loader2, 
  Palette, 
  MessageSquare, 
  User, 
  Settings2,
  Plus,
  X,
  Save,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { WebchatPreview } from './WebchatPreview';
import { WidgetCodeGenerator } from './WidgetCodeGenerator';
import { MediaUrlInput } from '@/components/ui/media-url-input';

// Interface para configuração do webchat
interface WebchatConfig {
  id?: string;
  inbox_id: string;
  tenant_id: string;
  is_enabled: boolean;
  primary_color: string;
  secondary_color: string;
  text_color: string;
  position: 'bottom-right' | 'bottom-left';
  button_icon: 'chat' | 'message' | 'support' | 'custom';
  custom_icon_url: string | null;
  greeting_message: string;
  offline_message: string;
  placeholder_text: string;
  company_name: string;
  company_logo_url: string | null;
  auto_open_delay: number;
  require_email: boolean;
  require_phone: boolean;
  require_name: boolean;
  allowed_domains: string[];
  welcome_flow_id: string | null;
  show_branding: boolean;
  custom_css: string | null;
  // Novas opções visuais do botão flutuante
  button_style: 'solid' | 'border-logo';
  button_border_width: number;
  // Novas opções de avatar no header
  header_avatar_mode: 'logo' | 'agent';
  header_logo_url: string | null;
  // Permite ao visitante minimizar o widget em mini-pill
  allow_minimize: boolean;
}

// Valores padrão para nova configuração
const defaultConfig: Omit<WebchatConfig, 'inbox_id' | 'tenant_id'> = {
  is_enabled: true,
  primary_color: '#3B82F6',
  secondary_color: '#1E40AF',
  text_color: '#FFFFFF',
  position: 'bottom-right',
  button_icon: 'chat',
  custom_icon_url: null,
  greeting_message: 'Olá! Como podemos ajudar?',
  offline_message: 'No momento estamos offline. Deixe sua mensagem!',
  placeholder_text: 'Digite sua mensagem...',
  company_name: 'Suporte',
  company_logo_url: null,
  auto_open_delay: 0,
  require_email: false,
  require_phone: false,
  require_name: true,
  allowed_domains: [],
  welcome_flow_id: null,
  show_branding: true,
  custom_css: null,
  // Novos valores padrão
  button_style: 'solid',
  button_border_width: 4,
  header_avatar_mode: 'agent',
  header_logo_url: null,
  allow_minimize: false,
};

interface WebchatConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inboxId: string;
  tenantId: string;
  inboxName: string;
}

export function WebchatConfigDialog({
  open,
  onOpenChange,
  inboxId,
  tenantId,
  inboxName,
}: WebchatConfigDialogProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<WebchatConfig>({
    ...defaultConfig,
    inbox_id: inboxId,
    tenant_id: tenantId,
  });
  const [newDomain, setNewDomain] = useState('');
  const [flows, setFlows] = useState<{ id: string; name: string }[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showCodeGenerator, setShowCodeGenerator] = useState(false);

  // Carrega configuração existente ou cria nova
  useEffect(() => {
    if (open && inboxId) {
      fetchConfig();
      fetchFlows();
    }
  }, [open, inboxId]);

  /**
   * Busca configuração existente do webchat
   */
  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('webchat_configs')
        .select('*')
        .eq('inbox_id', inboxId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = not found, que é esperado para nova inbox
        throw error;
      }

      if (data) {
        setConfig({
          ...data,
          position: (data.position as 'bottom-right' | 'bottom-left') || 'bottom-right',
          button_icon: (data.button_icon as 'chat' | 'message' | 'support' | 'custom') || 'chat',
          button_style: (data.button_style as 'solid' | 'border-logo') || 'solid',
          header_avatar_mode: (data.header_avatar_mode as 'logo' | 'agent') || 'agent',
          allowed_domains: data.allowed_domains || [],
          allow_minimize: (data as any).allow_minimize ?? false,
        });
      } else {
        // Usa valores padrão para nova configuração
        setConfig({
          ...defaultConfig,
          inbox_id: inboxId,
          tenant_id: tenantId,
        });
      }
    } catch (error) {
      logger.error('WebchatConfigDialog', 'Erro ao carregar configuração', error);
      toast.error('Erro ao carregar configuração do widget');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Busca fluxos disponíveis para welcome_flow_id
   */
  const fetchFlows = async () => {
    try {
      const { data, error } = await supabase
        .from('flows')
        .select('id, name')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      setFlows(data || []);
    } catch (error) {
      logger.warn('WebchatConfigDialog', 'Erro ao carregar fluxos', error);
    }
  };

  /**
   * Salva configuração do webchat via RPC
   */
  const handleSave = async () => {
    setSaving(true);
    try {
      const configPayload = {
        tenant_id: tenantId,
        is_enabled: config.is_enabled,
        primary_color: config.primary_color,
        secondary_color: config.secondary_color,
        text_color: config.text_color,
        position: config.position,
        button_icon: config.button_icon,
        custom_icon_url: config.custom_icon_url,
        greeting_message: config.greeting_message,
        offline_message: config.offline_message,
        placeholder_text: config.placeholder_text,
        company_name: config.company_name,
        company_logo_url: config.company_logo_url,
        auto_open_delay: config.auto_open_delay,
        require_email: config.require_email,
        require_phone: config.require_phone,
        require_name: config.require_name,
        allowed_domains: config.allowed_domains,
        welcome_flow_id: config.welcome_flow_id,
        show_branding: config.show_branding,
        custom_css: config.custom_css,
        // Novos campos visuais
        button_style: config.button_style,
        button_border_width: config.button_border_width,
        header_avatar_mode: config.header_avatar_mode,
        header_logo_url: config.header_logo_url,
        allow_minimize: config.allow_minimize,
      };

      const { error } = await supabase.rpc('upsert_webchat_config', {
        p_inbox_id: inboxId,
        p_config: configPayload,
      });

      if (error) throw error;

      toast.success('Configuração salva com sucesso!');
      onOpenChange(false);
    } catch (error) {
      logger.error('WebchatConfigDialog', 'Erro ao salvar configuração', error);
      toast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Adiciona domínio à lista de permitidos
   */
  const addDomain = () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;

    // Validação simples de domínio
    if (!/^[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,}$/.test(domain)) {
      toast.error('Domínio inválido');
      return;
    }

    if (config.allowed_domains.includes(domain)) {
      toast.warning('Domínio já adicionado');
      return;
    }

    setConfig(prev => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, domain],
    }));
    setNewDomain('');
  };

  /**
   * Remove domínio da lista
   */
  const removeDomain = (domain: string) => {
    setConfig(prev => ({
      ...prev,
      allowed_domains: prev.allowed_domains.filter(d => d !== domain),
    }));
  };

  /**
   * Atualiza campo da configuração
   */
  const updateConfig = <K extends keyof WebchatConfig>(
    key: K,
    value: WebchatConfig[K]
  ) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Configurar Widget - {inboxName}
            </DialogTitle>
            <DialogDescription>
              Personalize a aparência e comportamento do widget de chat do seu site.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="visual" className="mt-4">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="visual" className="text-xs sm:text-sm">
                <Palette className="h-4 w-4 mr-1" />
                Visual
              </TabsTrigger>
              <TabsTrigger value="behavior" className="text-xs sm:text-sm">
                <MessageSquare className="h-4 w-4 mr-1" />
                Comportamento
              </TabsTrigger>
              <TabsTrigger value="data" className="text-xs sm:text-sm">
                <User className="h-4 w-4 mr-1" />
                Coleta
              </TabsTrigger>
              <TabsTrigger value="advanced" className="text-xs sm:text-sm">
                <Settings2 className="h-4 w-4 mr-1" />
                Avançado
              </TabsTrigger>
            </TabsList>

            {/* Tab Visual */}
            <TabsContent value="visual" className="space-y-4 mt-4">
              {/* Widget ativo */}
              <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                <div>
                  <Label>Widget Ativo</Label>
                  <p className="text-sm text-muted-foreground">
                    Ativa ou desativa o widget no site
                  </p>
                </div>
                <Switch
                  checked={config.is_enabled}
                  onCheckedChange={(checked) => updateConfig('is_enabled', checked)}
                />
              </div>

              {/* Cores */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Cor Principal</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primary_color"
                      type="color"
                      value={config.primary_color}
                      onChange={(e) => updateConfig('primary_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={config.primary_color}
                      onChange={(e) => updateConfig('primary_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary_color">Cor Secundária</Label>
                  <div className="flex gap-2">
                    <Input
                      id="secondary_color"
                      type="color"
                      value={config.secondary_color}
                      onChange={(e) => updateConfig('secondary_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={config.secondary_color}
                      onChange={(e) => updateConfig('secondary_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="text_color">Cor do Texto</Label>
                  <div className="flex gap-2">
                    <Input
                      id="text_color"
                      type="color"
                      value={config.text_color}
                      onChange={(e) => updateConfig('text_color', e.target.value)}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={config.text_color}
                      onChange={(e) => updateConfig('text_color', e.target.value)}
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Nome e Logo da empresa */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nome da Empresa</Label>
                  <Input
                    id="company_name"
                    value={config.company_name}
                    onChange={(e) => updateConfig('company_name', e.target.value)}
                    placeholder="Nome exibido no header"
                  />
                </div>
                <MediaUrlInput
                  value={config.company_logo_url}
                  onChange={(url) => updateConfig('company_logo_url', url)}
                  label="URL do Logo"
                  placeholder="https://exemplo.com/logo.png"
                  showPreview={true}
                  allowedTypes={['image']}
                />
              </div>

              {/* Posição e Ícone */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Posição do Botão</Label>
                  <Select
                    value={config.position}
                    onValueChange={(v) => updateConfig('position', v as 'bottom-right' | 'bottom-left')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom-right">Inferior Direito</SelectItem>
                      <SelectItem value="bottom-left">Inferior Esquerdo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Ícone do Botão</Label>
                  <Select
                    value={config.button_icon}
                    onValueChange={(v) => updateConfig('button_icon', v as 'chat' | 'message' | 'support' | 'custom')}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chat">💬 Chat</SelectItem>
                      <SelectItem value="message">✉️ Mensagem</SelectItem>
                      <SelectItem value="support">🎧 Suporte</SelectItem>
                      <SelectItem value="custom">🎨 Customizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* URL do ícone customizado */}
              {config.button_icon === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom_icon_url">URL do Ícone Customizado</Label>
                  <Input
                    id="custom_icon_url"
                    value={config.custom_icon_url || ''}
                    onChange={(e) => updateConfig('custom_icon_url', e.target.value || null)}
                    placeholder="https://exemplo.com/icone.svg"
                  />
                </div>
              )}

              {/* NOVA SEÇÃO: Estilo do Botão Flutuante */}
              <div className="space-y-4 p-4 rounded-lg border bg-muted/20">
                <div>
                  <Label className="text-base font-semibold">Estilo do Botão</Label>
                  <p className="text-sm text-muted-foreground">
                    Escolha como o botão flutuante do chat será exibido
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Estilo Visual</Label>
                    <Select
                      value={config.button_style}
                      onValueChange={(v) => updateConfig('button_style', v as 'solid' | 'border-logo')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solid">Cor Sólida com Ícone</SelectItem>
                        <SelectItem value="border-logo">Borda Colorida com Logo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {config.button_style === 'border-logo' && (
                    <div className="space-y-2">
                      <Label htmlFor="button_border_width">Largura da Borda (px)</Label>
                      <Input
                        id="button_border_width"
                        type="number"
                        min={1}
                        max={10}
                        value={config.button_border_width}
                        onChange={(e) => updateConfig('button_border_width', parseInt(e.target.value) || 4)}
                      />
                    </div>
                  )}
                </div>

                {/* Toggle: Permitir minimizar widget */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="space-y-0.5 pr-4">
                    <Label htmlFor="allow_minimize" className="text-sm font-medium">
                      Permitir minimizar
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Exibe um "X" no botão flutuante para o visitante recolher em uma mini-pill discreta.
                    </p>
                  </div>
                  <Switch
                    id="allow_minimize"
                    checked={config.allow_minimize}
                    onCheckedChange={(checked) => updateConfig('allow_minimize', checked)}
                  />
                </div>
              </div>

              {/* NOVA SEÇÃO: Avatar no Header */}
              <div className="space-y-4 p-4 rounded-lg border bg-muted/20">
                <div>
                  <Label className="text-base font-semibold">Avatar no Header</Label>
                  <p className="text-sm text-muted-foreground">
                    Configure o avatar exibido no cabeçalho do chat
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Modo do Avatar</Label>
                    <Select
                      value={config.header_avatar_mode}
                      onValueChange={(v) => updateConfig('header_avatar_mode', v as 'logo' | 'agent')}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="logo">Sempre mostrar Logo</SelectItem>
                        <SelectItem value="agent">Foto do Agente (quando disponível)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <MediaUrlInput
                      value={config.header_logo_url}
                      onChange={(url) => updateConfig('header_logo_url', url)}
                      label="URL da Logo (Header/Botão)"
                      placeholder="https://exemplo.com/logo.png"
                      showPreview={true}
                      allowedTypes={['image']}
                    />
                    <p className="text-xs text-muted-foreground">
                      Usada no header e no botão (modo borda com logo)
                    </p>
                  </div>
                </div>
              </div>

              {/* Branding - sempre exibe "Powered by UaizyChat" */}
            </TabsContent>

            {/* Tab Comportamento */}
            <TabsContent value="behavior" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="greeting_message">Mensagem de Boas-vindas</Label>
                <Textarea
                  id="greeting_message"
                  value={config.greeting_message}
                  onChange={(e) => updateConfig('greeting_message', e.target.value)}
                  placeholder="Mensagem exibida ao abrir o chat"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="offline_message">Mensagem Offline</Label>
                <Textarea
                  id="offline_message"
                  value={config.offline_message}
                  onChange={(e) => updateConfig('offline_message', e.target.value)}
                  placeholder="Mensagem quando não há agentes online"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="placeholder_text">Placeholder do Campo</Label>
                <Input
                  id="placeholder_text"
                  value={config.placeholder_text}
                  onChange={(e) => updateConfig('placeholder_text', e.target.value)}
                  placeholder="Texto exibido no campo de mensagem"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="auto_open_delay">
                  Delay para Abrir Automaticamente (segundos)
                </Label>
                <Input
                  id="auto_open_delay"
                  type="number"
                  min={0}
                  value={config.auto_open_delay}
                  onChange={(e) => updateConfig('auto_open_delay', parseInt(e.target.value) || 0)}
                />
                <p className="text-sm text-muted-foreground">
                  0 = desativado. O widget abrirá automaticamente após X segundos.
                </p>
              </div>
            </TabsContent>

            {/* Tab Coleta de Dados */}
            <TabsContent value="data" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                Configure quais informações serão solicitadas antes de iniciar o chat.
              </p>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <Label>Exigir Nome</Label>
                    <p className="text-sm text-muted-foreground">
                      Solicita o nome do visitante antes de iniciar
                    </p>
                  </div>
                  <Switch
                    checked={config.require_name}
                    onCheckedChange={(checked) => updateConfig('require_name', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <Label>Exigir Email</Label>
                    <p className="text-sm text-muted-foreground">
                      Solicita o email do visitante (validado)
                    </p>
                  </div>
                  <Switch
                    checked={config.require_email}
                    onCheckedChange={(checked) => updateConfig('require_email', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <Label>Exigir Telefone</Label>
                    <p className="text-sm text-muted-foreground">
                      Solicita o telefone do visitante
                    </p>
                  </div>
                  <Switch
                    checked={config.require_phone}
                    onCheckedChange={(checked) => updateConfig('require_phone', checked)}
                  />
                </div>
              </div>
            </TabsContent>

            {/* Tab Avançado */}
            <TabsContent value="advanced" className="space-y-4 mt-4">
              {/* Domínios Permitidos */}
              <div className="space-y-2">
                <Label>Domínios Permitidos</Label>
                <p className="text-sm text-muted-foreground">
                  Restrinja o widget a domínios específicos. Deixe vazio para permitir todos.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="exemplo.com.br"
                    onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                  />
                  <Button type="button" onClick={addDomain} size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {config.allowed_domains.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {config.allowed_domains.map((domain) => (
                      <Badge key={domain} variant="secondary" className="gap-1">
                        {domain}
                        <button
                          type="button"
                          onClick={() => removeDomain(domain)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Fluxo de Boas-vindas */}
              <div className="space-y-2">
                <Label>Fluxo de Boas-vindas</Label>
                <p className="text-sm text-muted-foreground">
                  Dispara um fluxo automaticamente quando um visitante inicia o chat.
                </p>
                <Select
                  value={config.welcome_flow_id || 'none'}
                  onValueChange={(v) => updateConfig('welcome_flow_id', v === 'none' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um fluxo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {flows.map((flow) => (
                      <SelectItem key={flow.id} value={flow.id}>
                        {flow.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* CSS Customizado */}
              <div className="space-y-2">
                <Label htmlFor="custom_css">CSS Customizado</Label>
                <p className="text-sm text-muted-foreground">
                  CSS adicional para personalização avançada.
                </p>
                <Textarea
                  id="custom_css"
                  value={config.custom_css || ''}
                  onChange={(e) => updateConfig('custom_css', e.target.value || null)}
                  placeholder=".uaizychat-widget { /* seu CSS */ }"
                  rows={5}
                  className="font-mono text-sm"
                />
              </div>
            </TabsContent>
          </Tabs>

          {/* Botões de ação */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPreview(true)}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCodeGenerator(true)}
              >
                Obter Código
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Save className="h-4 w-4 mr-2" />
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <WebchatPreview
        open={showPreview}
        onOpenChange={setShowPreview}
        config={config}
      />

      {/* Code Generator Dialog */}
      <WidgetCodeGenerator
        open={showCodeGenerator}
        onOpenChange={setShowCodeGenerator}
        inboxId={inboxId}
      />
    </>
  );
}
